import React, { useEffect, useState } from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classNames from 'classnames'
import { Case, Else, If, Switch, Then, When } from 'react-if'
import { ButtonVariant } from '@/atoms/Button'
import { CheckBox } from '@/atoms/CheckBox'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { RadioButtonIcon } from '@/atoms/Icons/RadioButtonIcon'
import { RadioButtonUncheckedIcon } from '@/atoms/Icons/RadioButtonUncheckedIcon'
import { Image } from '@/atoms/Image'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { CaptionLG, CaptionMD, CaptionSM, CaptionXS, LabelSM, TitleSM } from '@/atoms/Text'
import { GuildOffer } from '@/constants/guild'
import { BoundedImage } from '@/molecules/BoundedImage'
import { GuildSignupButton } from '@/molecules/GuildSignupButton'
import { options } from '@/services/ContentfulService'
import { useGuildMember } from '@/services/GuildUserService'
import { formatPrice } from '@/services/PriceFormatter'
import { formatPromotionPriceDisplay, getPromotionalPrice, ValidatedPromotion } from '@/services/SaleService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'
import { OmniGuildPricingLabel } from '@/views/GuildSignupView/OmniGuildPlanSelectionCard/OmniGuildPlanBox/OmniGuildPricingLabel'
import { OmniPricePerInterval } from '@/views/GuildSignupView/OmniGuildPlanSelectionCard/OmniGuildPlanBox/OmniGuildPricingLabel/OmniGuildPricingLabel'

export interface GuildPlanBoxProps {
  className?: string
  offers: GuildOffer[]
  orderClass?: string
  disclosePriceInUSD?: boolean
  defaultSelectedOffer?: OfferTier
  promotedInterval?: 'monthly' | 'annual'
}

export type OfferTier = 'basic' | 'basic-no-ads' | 'premium'

export const OmniGuildPlanBox: React.FC<GuildPlanBoxProps> = ({
  className,
  offers,
  disclosePriceInUSD = false,
  defaultSelectedOffer = 'premium',
  promotedInterval,
}) => {
  const { t } = useTranslate('guild')
  const { isGuildMember } = useGuildMember()
  const { sale } = useGuildSignupContext()
  const [selectedOffer, setSelectedOffer] = useState<OfferTier>(defaultSelectedOffer)

  // Update selected offer when defaultSelectedOffer prop changes
  useEffect(() => {
    setSelectedOffer(defaultSelectedOffer)
  }, [defaultSelectedOffer])

  const offer = offers.find((o) => o.tier === selectedOffer) || offers[0]
  const yearlyPromotion = sale?.promotions.find((p) => p.offer.id === offer?.annual.bimOfferId)
  const monthlyPromotion = sale?.promotions.find((p) => p.offer.id === offer?.monthly.bimOfferId)

  const SELECTED_BG_CLASS_MAP = {
    basic: 'border-2 border-oxide bg-core-gray-100',
    'basic-no-ads': 'border-2 border-oxide bg-core-gray-100',
    premium: 'border-2 border-oxide bg-core-gray-100',
  }

  const orderedOffers = offers.toSorted((a, b) => a.monthly.standardPrice - b.monthly.standardPrice)

  const { locale } = useLocale()

  if (!offer) {
    return null
  }

  const getMonthlyPrice = (offer: GuildOffer) => {
    const offerId = offer.monthly.bimOfferId
    const promotion = sale?.promotions.find((p) => p.offer.id === offerId) as ValidatedPromotion
    const isPromoApplicable = Boolean(promotion)

    if (isPromoApplicable) {
      return formatPromotionPriceDisplay(promotion, locale)
    }
    return formatPrice(offer.monthly.standardPrice, { locale, includeDecimals: false, currency: 'USD' })
  }

  return (
    <div className={classNames(className, 'flex justify-center items-center w-full px-1.5 se:px-1 md:px-3')}>
      <div className="relative h-full max-w-[520px] flex-col overflow-hidden rounded-2xl bg-white p-2 text-left text-core-gray-950 md:flex md:p-4 md:px-3 se:px-1">
        <CaptionXS weight="bold">Choose your plan</CaptionXS>
        <div className="relative mt-2 flex w-full items-center justify-between gap-2">
          <div className="grid w-full grid-cols-3 gap-1">
            {orderedOffers.map((off, id) => (
              <div key={`${off.tier}-${id}`} className="relative col-span-1 h-[120px] rounded-lg">
                <When condition={off.tier === 'premium'}>
                  <span className="absolute left-1/2 top-[-20px] h-5 w-[80%] -translate-x-1/2 rounded-t-md bg-[#DFAF89] pt-1 text-center text-[10px] font-bold">
                    {t('mostPopular', 'Most Popular')}
                  </span>
                </When>
                <button
                  className={classNames(
                    'flex flex-col items-start justify-between rounded-lg border p-2 md:p-4 md:pt-2 text-start w-full h-full outline-1 outline-transparent',
                    off.tier === selectedOffer
                      ? SELECTED_BG_CLASS_MAP[off.tier]
                      : 'border-2 border-core-gray-300 bg-core-gray-100',
                  )}
                  onClick={() => setSelectedOffer(off.tier)}
                >
                  <Switch>
                    <Case condition={off.tier === 'basic'}>
                      <div>
                        <CaptionSM className="md:photon-paragraph-md" color="black" weight="bold">
                          {t('basicWithAds', 'Basic with Ads')}
                        </CaptionSM>
                        <CaptionXS color="core-gray-800">{t('streamIn1080p', 'Stream in 1080p')}</CaptionXS>
                      </div>
                      <CaptionXS color="core-gray-800">
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                    <Case condition={off.tier === 'basic-no-ads'}>
                      <div>
                        <CaptionSM className="md:photon-paragraph-md" color="black" weight="bold">
                          {t('basic', 'Basic')}
                        </CaptionSM>
                        <CaptionXS color="core-gray-800">{t('streamIn1080p', 'Stream in 1080p')}</CaptionXS>
                      </div>
                      <CaptionXS color="core-gray-800">
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                    <Case condition={off.tier === 'premium'}>
                      <div>
                        <CaptionSM className="md:photon-paragraph-md" color="black" weight="bold">
                          {t('premium', 'Premium')}
                        </CaptionSM>
                        <CaptionXS color="core-gray-800">
                          {t('streamIn4KFreeMovieTicketsAndMore', 'Stream in 4K, Free Movie Tickets & More')}
                        </CaptionXS>
                      </div>
                      <CaptionXS color="core-gray-800">
                        from {getMonthlyPrice(off)}
                        /month
                      </CaptionXS>
                    </Case>
                  </Switch>
                  <div className="absolute right-1 top-2 md:hidden">
                    {off.tier === selectedOffer ? (
                      <RadioButtonIcon size={12} color="oxide" className="overflow-visible" />
                    ) : (
                      <RadioButtonUncheckedIcon size={12} color="black" className="overflow-visible" />
                    )}
                  </div>
                  <div className="absolute right-2 top-2 hidden md:top-3.5 md:block">
                    {off.tier === selectedOffer ? (
                      <RadioButtonIcon size={16} color="oxide" className="overflow-visible" />
                    ) : (
                      <RadioButtonUncheckedIcon size={16} color="black" className="overflow-visible" />
                    )}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 pb-4 pt-6">
          <ul className={classNames('flex flex-col gap-2')}>
            <When condition={offer.tier === 'basic'}>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="shareFreeAccessShowsFilms">
                    Share free access to shows and films with friends and family
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="limitedAds">
                    Limited Ads
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="streamInFullHd">
                    Stream in 1080p (Full HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="instantAccessWeeklyStoriesAboutCourageRedemptionHope">
                    <span className="font-semibold text-oxide">Instant</span> <strong>Access</strong> to weekly releases
                    of stories about courage, redemption, hope, and family strength, perfect for watching together
                  </Translate>
                </CaptionMD>
              </li>
            </When>
            <When condition={offer.tier === 'basic-no-ads'}>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="shareFreeAccessShowsFilms">
                    Share free access to shows and films with friends and family
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="adFreeHyphenated">
                    Ad-Free
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="streamInFullHd">
                    Stream in 1080p (Full HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="instantAccessWeeklyStoriesAboutCourageRedemptionHope">
                    <span className="font-semibold text-oxide">Instant</span> <strong>Access</strong> to weekly releases
                    of stories about courage, redemption, hope, and family strength, perfect for watching together
                  </Translate>
                </CaptionMD>
              </li>
            </When>
            <When condition={offer.tier === 'premium'}>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="shareFreeAccessShowsFilms">
                    Share free access to shows and films with friends and family
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="adFreeHyphenated">
                    Ad-Free
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="streamIn4kUltraHd">
                    Stream in 4K (Ultra HD)
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="voteOnShowsV1">
                    Vote on concepts and films to decide what we greenlight next
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="twoTicketsToEveryReleasev5">
                    Two tickets to every Angel movie in theaters to use or give away
                  </Translate>
                </CaptionMD>
              </li>
              <li className="flex items-start gap-2">
                <div className="mt-0.5 min-h-4 min-w-4 shrink-0 grow-0">
                  <CircleCheckIcon size={16} color1="black" className="shrink-0 grow-0" />
                </div>
                <CaptionMD>
                  <Translate t={t} i18nKey="instantAccessWeeklyStoriesAboutCourageRedemptionHope">
                    <span className="font-semibold text-oxide">Instant</span> <strong>Access</strong> to weekly releases
                    of stories about courage, redemption, hope, and family strength, perfect for watching together
                  </Translate>
                </CaptionMD>
              </li>
            </When>
          </ul>
        </div>
        <div className="mt-auto flex flex-col gap-2 pb-5 pt-4">
          <PlanSelectorWell
            className="order-1"
            offer={offer}
            currency="USD"
            promotion={monthlyPromotion}
            planInterval="monthly"
            isGuildMember={isGuildMember}
            punchyButtonColors
            disclosePriceInUSD={disclosePriceInUSD}
            promotedInterval={promotedInterval}
          />
          <PlanSelectorWell
            className="order-0"
            currency="USD"
            offer={offer}
            promotion={yearlyPromotion}
            planInterval="annual"
            isGuildMember={isGuildMember}
            punchyButtonColors
            disclosePriceInUSD={disclosePriceInUSD}
            promotedInterval={promotedInterval}
          />
        </div>
      </div>
    </div>
  )
}

interface PlanSelectorWellProps {
  currency: string
  promotion?: ValidatedPromotion
  isGuildMember?: boolean
  planInterval: 'monthly' | 'annual'
  punchyButtonColors?: boolean
  disclosePriceInUSD?: boolean
  className?: string
  offer: GuildOffer
  promotedInterval?: 'monthly' | 'annual'
}

function useButtonStyles({
  planInterval,
  promotedInterval = 'annual',
}: {
  planInterval: 'monthly' | 'annual'
  promotedInterval?: 'monthly' | 'annual'
}) {
  const buttonClassName = ''

  const buttonVariant: ButtonVariant = planInterval === promotedInterval ? 'black' : 'plain'

  return { buttonClassName, buttonVariant }
}

export const PlanSelectorWell: React.FC<PlanSelectorWellProps> = ({
  currency,
  promotion,
  planInterval,
  isGuildMember,
  disclosePriceInUSD,
  className,
  offer,
  promotedInterval,
}) => {
  const { t } = useTranslate('guild')
  const { locale } = useLocale()
  const { slug, guildPricingTicketsOption, experiments } = useGuildSignupContext()
  const router = useSafeRouter()

  // This component usually loads below the fold, so we only load images when it's in view.
  // On the guild pricing page, we want to load images eagerly because this component is above the fold on that page.
  const isGuildPricingPage = router.pathname.endsWith('/guild/pricing')

  const shouldShowExtraTickets = guildPricingTicketsOption && planInterval === 'annual'

  const { buttonClassName, buttonVariant } = useButtonStyles({
    planInterval,
    promotedInterval,
  })
  const canShowTMobileLogo = planInterval === 'monthly' && offer.tier === 'premium' && slug === 'tmtdec25'

  // Coupon test experiment
  const couponTestVariation =
    typeof experiments['coupon-test-member-selection'] === 'string' ? experiments['coupon-test-member-selection'] : ''
  const isCouponVariation1 = couponTestVariation === 'variation_1'
  const isCouponVariation2 = couponTestVariation === 'variation_2'
  const isCouponTestActive = isCouponVariation1 || isCouponVariation2
  const [couponApplied, setCouponApplied] = useState(false)

  const showCouponCheckbox = isCouponTestActive && planInterval === 'annual'
  const showFullPriceOnly = showCouponCheckbox && !couponApplied

  // Calculate coupon display values
  const promoPrice = promotion ? getPromotionalPrice(promotion) : null
  const effectivePrice = promoPrice ?? offer.annual.standardPrice
  const couponSavings = offer.annual.fullPrice - effectivePrice
  const formattedCouponSavings = formatPrice(couponSavings, { locale, includeDecimals: false, currency })
  const formattedCouponPrice = formatPrice(effectivePrice, { locale, includeDecimals: false, currency })

  // When coupon test is active and coupon is not applied, don't pass promo code
  const effectivePromoCode =
    showCouponCheckbox && !couponApplied ? null : promotion ? promotion.promotionCode.code : null

  return (
    <div
      className={classNames(
        'relative rounded-lg p-4 bg-core-gray-100 border border-core-gray-300',
        { '!border-[#E20074] !border-2': canShowTMobileLogo },
        className,
      )}
    >
      <div className="flex">
        <div className="grow-1">
          <If condition={planInterval === 'annual'}>
            <Then>
              <CaptionLG weight="semibold" className="uppercase">
                <Translate i18nKey="annual" t={t}>
                  Annual
                </Translate>
              </CaptionLG>
            </Then>
            <Else>
              <CaptionLG weight="semibold" className="uppercase">
                <Translate i18nKey="monthly" t={t}>
                  Monthly
                </Translate>
              </CaptionLG>
            </Else>
          </If>
          <OmniPricePerInterval
            className="mb-2"
            planInterval={planInterval}
            disclosePriceInUSD={disclosePriceInUSD}
            offer={offer}
            showFullPriceOnly={showFullPriceOnly}
          />
          <div className="-mb-3">
            <OmniGuildPricingLabel
              currency={currency}
              planInterval={planInterval}
              offer={offer}
              showFullPriceOnly={showFullPriceOnly}
              hideSocialProofText={showCouponCheckbox}
            />
          </div>
          <When condition={showCouponCheckbox}>
            <div className="mt-4">
              <CouponCheckbox
                checked={couponApplied}
                onChange={() => setCouponApplied((prev) => !prev)}
                variant={isCouponVariation1 ? 'apply-coupon' : 'coupon-price'}
                formattedSavings={formattedCouponSavings}
                formattedPrice={formattedCouponPrice}
              />
            </div>
          </When>
        </div>
        <div className="ml-auto w-fit grow-0">
          <GuildSignupButton
            currency={currency}
            offerId={offer[planInterval].bimOfferId}
            price={offer[planInterval].standardPrice}
            promoCode={effectivePromoCode}
            planInterval={planInterval}
            className={classNames('ml-auto !w-fit', buttonClassName)}
            buttonVariant={buttonVariant}
          >
            <LabelSM className="capitalize">
              <If condition={isGuildMember}>
                <Then>
                  <Translate t={t} i18nKey="upgradeMembership">
                    Upgrade Membership
                  </Translate>
                </Then>
                <Else>
                  <Translate t={t} i18nKey="select">
                    Select
                  </Translate>
                </Else>
              </If>
            </LabelSM>
          </GuildSignupButton>
          {canShowTMobileLogo && (
            <div className="ml-auto mt-2 h-[40px] w-[63px]">
              <BoundedImage
                className="flex h-full w-full rounded-xl"
                alt={t('tMobileLogo', 'T-mobile logo')}
                path={'https://images.angelstudios.com/image/upload/v1765302394/angel-studios/misc-icons/TMT-logo.png'}
                width={63}
                height={40}
                sizes="63px"
                imageClassName="h-full w-full object-contain"
              />
            </div>
          )}
        </div>
      </div>
      <When condition={shouldShowExtraTickets}>
        <div className="-mb-4 pt-4">
          <div className="relative overflow-hidden border-t border-core-gray-500">
            <div className="flex">
              <div className="py-4 pr-4">
                {guildPricingTicketsOption?.title && (
                  <TitleSM color="black" weight="bold" className="mb-1">
                    {guildPricingTicketsOption.title}

                    <If condition={offer.tier === 'premium'}>
                      <Then>
                        <CaptionSM weight="semibold" color="core-gray-800">
                          <Translate t={t} i18nKey="fourTotalWithPremium60Value">
                            4 Total with Premium, <span className="font-bold text-core-oxide">$60 value</span>
                          </Translate>
                        </CaptionSM>
                      </Then>
                      <Else>
                        <CaptionSM weight="semibold" color="core-gray-800">
                          <Translate t={t} i18nKey="30Value">
                            <span className="font-bold text-core-oxide">$30 value</span>
                          </Translate>
                        </CaptionSM>
                      </Else>
                    </If>
                  </TitleSM>
                )}
                {guildPricingTicketsOption?.offerText?.json && (
                  <LazyHydrate whenVisible>
                    <CaptionMD color="core-gray-700" className="[&>p]:!photon-caption-md">
                      {documentToReactComponents(guildPricingTicketsOption.offerText.json, options)}
                    </CaptionMD>
                  </LazyHydrate>
                )}
              </div>
              <div className="max-h-[125px] w-1/2 content-end items-stretch overflow-hidden lg:w-2/3">
                {guildPricingTicketsOption?.ticketsImageUrl && (
                  <Image
                    alt="Two Bonus Tickets"
                    src={getCloudinaryImageUrl({ path: guildPricingTicketsOption.ticketsImageUrl })}
                    className="w-full object-cover"
                    width={450}
                    height={279}
                    loading={isGuildPricingPage ? 'eager' : 'lazy'}
                    sizes="(min-width: 1024px) 33vw, 50vw"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </When>
    </div>
  )
}

interface CouponCheckboxProps {
  checked: boolean
  onChange: () => void
  variant: 'apply-coupon' | 'coupon-price'
  formattedSavings: string
  formattedPrice: string
}

function CouponCheckbox({ checked, onChange, variant, formattedSavings, formattedPrice }: CouponCheckboxProps) {
  const { t } = useTranslate('guild')

  return (
    <div className="inline-flex rounded border border-dashed border-oxide px-2 py-1">
      <CheckBox
        id="coupon-test-checkbox"
        checked={checked}
        onChange={onChange}
        size="sm"
        wrapperClassName="gap-1.5"
        labelClassName="cursor-pointer text-sm font-semibold"
        checkedClassName="!w-4 !h-4 !min-w-4 !min-h-4"
        uncheckedClassName="!w-4 !h-4 !min-w-4 !min-h-4 !border-oxide"
      >
        {variant === 'apply-coupon' ? (
          <span className="text-oxide">
            {t('applyCouponAmount', 'Apply {{amount}} Coupon', { amount: formattedSavings })}
          </span>
        ) : (
          <span className="text-core-gray-950">
            {t('couponPriceAmount', '{{price}} Coupon Price', { price: formattedPrice })}
          </span>
        )}
      </CheckBox>
    </div>
  )
}
